import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import "./Events.css";
import { Link } from "react-router-dom";
import axios from "../../api/axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGithub } from "@fortawesome/free-brands-svg-icons";

const DATA_URL = "/data";

const Events = () => {
  const [projects, setProjects] = useState([]);

  // SEO using Helmet
  const pageTitle = "Projects || Karan Kumar Agrawal";

  useEffect(() => {
    document.title = pageTitle;
    const fetchProjectData = async () => {
      try {
        const response = await axios.get(`${DATA_URL}/projects`);
        setProjects(response.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchProjectData();
  }, []);

  // Get unique project types and place "Miscellaneous" at the end
  const projectTypes = useMemo(
    () =>
      [...new Set(projects.map((event) => event.type))].sort((a, b) =>
        a === "Miscellaneous" ? 1 : b === "Miscellaneous" ? -1 : 0
      ),
    [projects]
  );

  return (
    <main className="events-main">
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content="Portfolio of Karan Kumar Agrawal showcasing web apps, projects, and tech work. Explore details of each project with links and GitHub."
        />
        <meta
          name="keywords"
          content="Karan Kumar Agrawal, Portfolio, Projects, Web Apps, GitHub, Full Stack Developer"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://karan.inditech.in/projects" />

        {/* JSON-LD structured data for projects */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Karan Kumar Agrawal Projects",
            "url": "https://karan.inditech.in/projects",
            "itemListElement": projects.map((project, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "url": `/projects/${project.name.toLowerCase().replace(/\s+/g, "")}`,
              "name": project.name,
              "description": project.headline,
            })),
          })}
        </script>
      </Helmet>

      <h1 className="events-heading-text">My Projects</h1>

      {projectTypes.map((type, index) => (
        <section className="events-core" key={index}>
          <header className="events-heading">
            <hr />
            <h2 className="events-heading-text" id={`project-type-${index}`}>
              {type}
            </h2>
            <hr />
          </header>

          <div className="events-cards-row">
            {projects
              .filter((item) => item.type === type)
              .map((card, idx) => (
                <article className="events-vertical-card" key={idx}>
                  {card.icon && (
                    <div className="events-vertical-card-top">
                      <img
                        className="events-vertical-card-userimage"
                        src={card.icon}
                        alt={`${card.name} Logo`}
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="events-vertical-card-bottom">
                    <h3 className="events-vertical-name-text">{card.name}</h3>
                    <p className="events-vertical-name-desc">{card.headline}</p>
                  </div>
                  <div className="events-buttons">
                    <Link
                      to={`/projects/${card.name.toLowerCase().replace(/\s+/g, "")}`}
                      aria-label={`Learn more about ${card.name}`}
                    >
                      <button className="events-button" type="button">
                        Learn More
                      </button>
                    </Link>
                    {card.link && (
                      <a
                        href={card.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`See live project: ${card.name}`}
                      >
                        <button className="events-button" type="button">
                          See Live
                        </button>
                      </a>
                    )}
                    {type !== "Websites" && card.github && (
                      <a
                        href={card.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`GitHub repository for ${card.name}`}
                      >
                        <FontAwesomeIcon
                          className="events-github-button"
                          icon={faGithub}
                        />
                      </a>
                    )}
                  </div>
                </article>
              ))}
          </div>
        </section>
      ))}
    </main>
  );
};

export default Events;
